import { useEffect, useState } from 'react'

import { get } from '../../lib/api.ts'
import type { AuthProviders } from '../../../shared/auth.ts'

/**
 * Whether this deployment has a Google OAuth client configured.
 *
 * `undefined` while unknown, and the button renders nothing until it is known. A button
 * that appears and then vanishes is worse than one that arrives a beat late — the second
 * is a load, the first looks like the product changing its mind.
 */
export function useAuthProviders(): AuthProviders | undefined {
  const [providers, setProviders] = useState<AuthProviders | undefined>(undefined)

  useEffect(() => {
    let live = true
    get<AuthProviders>('/auth/providers')
      .then((p) => live && setProviders(p))
      // A deployment whose provider list cannot be read degrades to what is always safe:
      // no Google button, and accounts treated as closed.
      .catch(() => live && setProviders({ google: false, accountsOpen: false }))
    return () => {
      live = false
    }
  }, [])

  return providers
}

/**
 * "Continue with Google".
 *
 * An anchor and not a button, because this is a full-page navigation to Google and back —
 * `fetch` cannot do a redirect handshake, and an anchor gets middle-click, keyboard
 * activation and a visible target for free.
 */
export function GoogleButton({ next, label }: { next?: string | null; label: string }) {
  const providers = useAuthProviders()
  if (!providers?.google) return null

  const params = new URLSearchParams()
  if (next && next.startsWith('/')) params.set('next', next)
  const href = `/api/auth/google/start${params.size ? `?${params}` : ''}`

  return (
    <>
      <a className="btn outline block lg" href={href}>
        <GoogleMark />
        {label}
      </a>
      <Divider />
    </>
  )
}

/** The official four-colour mark, inline so it needs no network and no asset pipeline. */
function GoogleMark() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true" focusable="false">
      <path
        fill="#4285F4"
        d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18z"
      />
      <path
        fill="#FBBC05"
        d="M3.97 10.72a5.4 5.4 0 0 1 0-3.44V4.95H.96a9 9 0 0 0 0 8.1l3-2.33z"
      />
      <path
        fill="#EA4335"
        d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58C13.46.9 11.43 0 9 0A9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58z"
      />
    </svg>
  )
}

function Divider() {
  return (
    <div
      className="u-center u-faint u-sm"
      style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '18px 0' }}
    >
      <hr className="hr" style={{ flex: 1, margin: 0 }} />
      <span>or</span>
      <hr className="hr" style={{ flex: 1, margin: 0 }} />
    </div>
  )
}
